import { externalServices } from 'platform/monitoring/DowntimeNotification';
import environment from 'platform/utilities/environment';
import FormFooter from 'platform/forms/components/FormFooter';
import { VA_FORM_IDS } from 'platform/forms/constants';
import { blankSchema } from 'platform/forms-system/src/js/utilities/data/profile';
import { defaultItemPageScrollAndFocusTarget as scrollAndFocusTarget } from 'platform/forms-system/src/js/patterns/array-builder';
import { TITLE, SUBTITLE } from '../../utils/constants';
import manifest from '../../manifest.json';
import content from '../../utils/content.json';
import IntroductionPage from '../../containers/IntroductionPage';
import ConfirmationPage from '../../containers/ConfirmationPage';
import FormSavedPage from '../../containers/FormSavedPage';
import { defaultDefinitions } from '../../config/definitions';
import GetFormHelp from '../../components/GetFormHelp';
import ErrorText from '../../components/ErrorText';
import * as veteranInformation from './chapters/03-veteran-information';
import * as claimantInformation from '../../config/chapters/02-claimant-information';
import * as claimantInformationUpdate from './chapters/01-claimant-information';
import * as yourInformation from './chapters/02-claimant-information';
import * as householdInformation from './chapters/04-household-information';
import * as financialInformation from '../../config/chapters/06-financial-information';
import * as financialInformationUpdate from './chapters/05-financial-information';
import * as additionalInformation from '../../config/chapters/07-additional-information';
import {
  getCaveArtifactValidationErrors,
  hasVeteranInfoConflicts,
  hasMilitaryHistoryConflicts,
  caveUploadDocumentsPage,
  caveRequiredDocumentsPage,
  caveUploadRequiredDocumentsPage,
  caveConfirmVeteranInfoPage,
  caveConfirmMilitaryHistoryPage,
  ArtifactReviewPage,
} from '../../cave';
import IncorrectForm from '../../containers/IncorrectForm';
import { transform } from './submit-transformer';
import prefillTransformer from '../../config/prefill-transformer';
import { applyForceDivWrapperToFormConfig } from '../../config/uiSchemaHelpers';
import { focusH3 } from '../../utils/helpers';
import { isRelationshipToVeteran } from './utils/helpers';
import ReviewPageIntroduction from '../../components/ReviewPageIntroduction';

/** @type {FormConfig} */
const formConfig = {
  rootUrl: manifest.rootUrl,
  urlPrefix: '/',
  submitUrl: `${environment.API_URL}/survivors_benefits/v0/form534ez`,
  transformForSubmit: transform,
  trackingPrefix: 'survivors-534ez',
  useCustomScrollAndFocus: true,
  scrollAndFocusTarget: focusH3, // scroll and focus fallback
  v3SegmentedProgressBar: true,
  CustomReviewTopContent: ReviewPageIntroduction,
  prefillEnabled: true,
  prefillTransformer,
  dev: {
    disableWindowUnloadInCI: true,
    showNavLinks: true,
    collapsibleNavLinks: true,
  },
  downtime: {
    dependencies: [externalServices.icmhs],
  },
  formId: VA_FORM_IDS.FORM_21P_534EZ,
  saveInProgress: {
    messages: {
      inProgress: content['config-sip-in-progress'],
      expired: content['config-sip-in-expired'],
      saved: content['config-sip-saved'],
    },
  },
  version: 0,
  formSavedPage: FormSavedPage,
  defaultDefinitions,
  customValidationErrors: getCaveArtifactValidationErrors,
  savedFormMessages: {
    notFound: content['config-saved-not-found'],
    noAuth: content['config-saved-no-auth'],
  },
  preSubmitInfo: {
    statementOfTruth: {
      body: content['config-pre-submit-body'],
      messageAriaDescribedby: content['config-pre-submit-aria'],
      useProfileFullName: true,
      fullNamePath: 'yourName',
    },
  },
  title: TITLE,
  subTitle: SUBTITLE,
  introduction: IntroductionPage,
  confirmation: ConfirmationPage,
  footerContent: FormFooter,
  getHelp: GetFormHelp,
  errorText: ErrorText,
  showReviewErrors: !environment.isProduction(),
  chapters: {
    // Relationship to Veteran
    yourIdentity: {
      title: content['your-identity-chapter-title'],
      pages: {
        claimantRelationship: {
          path: 'claimant-relationship',
          title: content['claimant-relationship-title'],
          uiSchema: claimantInformationUpdate.claimantRelationship.uiSchema,
          schema: claimantInformationUpdate.claimantRelationship.schema,
        },
        claimantOther: {
          path: 'claimant-other',
          title: content['claimant-other-title'],
          CustomPage: IncorrectForm,
          CustomPageReview: null,
          uiSchema: {},
          schema: blankSchema,
          depends: formData => formData.claimantRelationship === 'OTHER',
        },
      },
    },
    // Required Documents
    requiredDocumentUpload: {
      title: "Veteran's DD214 and death certificate",
      hideOnReviewPage: true,
      pages: {
        requiredDocuments: {
          title: "Veteran's DD214 and death certificate",
          path: 'required-documents/overview',
          depends: formData =>
            formData?.['view:idpEnabled'] === true &&
            !formData?.survivorsBenefitsForm2026VersionEnabled,
          uiSchema: caveRequiredDocumentsPage.uiSchema,
          schema: caveRequiredDocumentsPage.schema,
        },
        uploadRequiredDocuments: {
          title: 'Upload DD214 and death certificate',
          path: 'required-documents/upload',
          depends: formData =>
            formData?.['view:idpEnabled'] === true &&
            !formData?.survivorsBenefitsForm2026VersionEnabled,
          uiSchema: caveUploadRequiredDocumentsPage.uiSchema,
          schema: caveUploadRequiredDocumentsPage.schema,
        },
      },
    },
    // Claimant Information
    claimantInformation: {
      title: ({ formData }) =>
        isRelationshipToVeteran(formData) === 'custodian'
          ? content['claimant-information-chapter-title-custodian']
          : content['claimant-information-chapter-title'],
      pages: {
        // TODO(phase 2): remove the prefill check after survivors_benefits_updated_prefill is 100% in prod
        personalInformation: {
          ...claimantInformation.claimantPersonalInformation
            .personalInformation,
          depends: formData =>
            formData?.survivorsBenefitsUpdatedPrefill === true &&
            isRelationshipToVeteran(formData) !== 'custodian',
        },
        ...yourInformation.contactInformation,
        // TODO(phase 2): remove the legacy yourInformation/yourIdentification
        // pages after survivors_benefits_updated_prefill is 100% in prod
        yourInformation: {
          path: 'your-information',
          title: ({ formData }) =>
            claimantInformation.yourInformationTitle(formData),
          depends: formData =>
            formData?.survivorsBenefitsUpdatedPrefill !== true,
          uiSchema: claimantInformation.yourInformation.uiSchema,
          schema: claimantInformation.yourInformation.schema,
        },
        yourIdentification: {
          path: 'your-identification',
          title: content['your-id-title'],
          depends: formData =>
            formData?.survivorsBenefitsUpdatedPrefill !== true &&
            isRelationshipToVeteran(formData) !== 'custodian',
          uiSchema: claimantInformation.yourIdentification.uiSchema,
          schema: claimantInformation.yourIdentification.schema,
        },
        // TODO(phase 2): remove the legacy yourContact/yourAddress pages after
        // survivors_benefits_updated_prefill is 100% in prod
        yourAddress: {
          path: 'your-address',
          title: content['your-address-title'],
          depends: formData =>
            formData?.survivorsBenefitsUpdatedPrefill !== true,
          uiSchema: claimantInformation.yourAddress.uiSchema,
          schema: claimantInformation.yourAddress.schema,
        },
        yourContact: {
          path: 'your-contact',
          title: content['your-contact-title'],
          depends: formData =>
            formData?.survivorsBenefitsUpdatedPrefill !== true,
          uiSchema: claimantInformation.yourContact.uiSchema,
          schema: claimantInformation.yourContact.schema,
        },
        childRelationship: {
          path: 'child-relationship',
          title: content['child-relationship-title'],
          depends: formData =>
            isRelationshipToVeteran(formData) === 'custodian',
          uiSchema: claimantInformation.childRelationship.uiSchema,
          schema: claimantInformation.childRelationship.schema,
        },
        yourHistory: {
          path: 'your-service-history',
          title: content['your-history-title'],
          depends: formData =>
            isRelationshipToVeteran(formData) !== 'custodian',
          uiSchema: claimantInformation.yourHistory.uiSchema,
          schema: claimantInformation.yourHistory.schema,
        },
        veteranRelationship: {
          path: 'your-information/veteran-relationship',
          title: 'Your relationship to the Veteran',
          depends: formData => isRelationshipToVeteran(formData) === 'child',
          uiSchema: yourInformation.veteranRelationship.uiSchema,
          schema: yourInformation.veteranRelationship.schema,
        },
        childAdditionalInformation: {
          path: 'your-information/additional-information',
          title: 'Additional information',
          depends: formData => isRelationshipToVeteran(formData) === 'child',
          uiSchema: yourInformation.additionalInformation.uiSchema,
          schema: yourInformation.additionalInformation.schema,
        },
        nursingHome: {
          title: content['claim-information-nursing-home-title'],
          path: 'nursing-home',
          uiSchema: yourInformation.nursingHome.uiSchema,
          schema: yourInformation.nursingHome.schema,
        },
        childInformation: {
          path: 'child-information',
          title: content['child-info-title'],
          depends: formData =>
            isRelationshipToVeteran(formData) === 'custodian',
          uiSchema: claimantInformation.childInformation.uiSchema,
          schema: claimantInformation.childInformation.schema,
        },
        childIdentification: {
          path: 'child-identification',
          title: content['child-id-title'],
          depends: formData =>
            isRelationshipToVeteran(formData) === 'custodian',
          uiSchema: claimantInformation.childIdentification.uiSchema,
          schema: claimantInformation.childIdentification.schema,
        },
        childAddress: {
          path: 'child-address',
          title: content['child-address-title'],
          depends: formData =>
            isRelationshipToVeteran(formData) === 'custodian',
          uiSchema: claimantInformation.childAddress.uiSchema,
          schema: claimantInformation.childAddress.schema,
        },
        childContact: {
          path: 'child-contact',
          title: content['child-contact-title'],
          depends: formData =>
            isRelationshipToVeteran(formData) === 'custodian',
          uiSchema: claimantInformation.childContact.uiSchema,
          schema: claimantInformation.childContact.schema,
        },
        benefitType: {
          path: 'claimant-benefit-type',
          title: 'Benefit options',
          uiSchema: yourInformation.benefitOptions.uiSchema,
          schema: yourInformation.benefitOptions.schema,
        },
      },
    },
    // Veteran Information
    veteranInformation: {
      title: 'Veteran’s information',
      pages: {
        veteranName: {
          title: content['veteran-name-title'],
          path: 'veteran',
          uiSchema: veteranInformation.veteranName.uiSchema,
          schema: veteranInformation.veteranName.schema,
        },
        veteranIdentification: {
          title: content['veteran-id-title'],
          path: 'veteran-identification',
          uiSchema: veteranInformation.veteranIdentification.uiSchema,
          schema: veteranInformation.veteranIdentification.schema,
        },
        veteranDeath: {
          title: 'Veteran’s death information',
          path: 'veteran-additional-information',
          uiSchema: veteranInformation.veteranDeath.uiSchema,
          schema: veteranInformation.veteranDeath.schema,
        },
        veteranServicePeriod: {
          path: 'service-period',
          title: content['military-service-period-title'],
          uiSchema: veteranInformation.veteranServicePeriod.uiSchema,
          schema: veteranInformation.veteranServicePeriod.schema,
        },
      },
    },
    // Household Information
    householdInformation: {
      title: content['household-chapter-title'],
      pages: {
        marriageToVeteran: {
          path: 'marriage-to-veteran',
          title: content['household-veteran-marriage-title'],
          depends: formData =>
            formData.claimantRelationship === 'SURVIVING_SPOUSE',
          uiSchema: householdInformation.marriageToVeteran.uiSchema,
          schema: householdInformation.marriageToVeteran.schema,
        },
        marriageToVeteranLocation: {
          path: 'marriage-to-veteran/date-location',
          title: content['household-veteran-marriage-location-title'],
          depends: formData =>
            formData.claimantRelationship === 'SURVIVING_SPOUSE',
          uiSchema: householdInformation.marriageToVeteranLocation.uiSchema,
          schema: householdInformation.marriageToVeteranLocation.schema,
        },
        marriageToVeteranInfo: {
          path: 'marriage-to-veteran/info',
          title: content['household-veteran-marriage-info-title'],
          depends: formData =>
            formData.claimantRelationship === 'SURVIVING_SPOUSE',
          uiSchema: householdInformation.marriageToVeteranInfo.uiSchema,
          schema: householdInformation.marriageToVeteranInfo.schema,
          scrollAndFocusTarget,
        },
        legalStatusOfMarriage: {
          path: 'legal-status-of-marriage',
          title: content['household-legal-status-title'],
          depends: formData =>
            formData.claimantRelationship === 'SURVIVING_SPOUSE',
          uiSchema: householdInformation.legalStatusOfMarriage.uiSchema,
          schema: householdInformation.legalStatusOfMarriage.schema,
        },
        marriageToVeteranEnd: {
          path: 'marriage-to-veteran/end-in-divorce',
          title: 'Did your marriage end in divorce?',
          depends: formData =>
            formData.claimantRelationship === 'SURVIVING_SPOUSE' &&
            !formData.marriedToVeteranAtTimeOfDeath,
          uiSchema: householdInformation.marriageToVeteranEnd.uiSchema,
          schema: householdInformation.marriageToVeteranEnd.schema,
          scrollAndFocusTarget,
        },
        marriageToVeteranEndInfo: {
          path: 'marriage-to-veteran/end-date-location',
          title: content['household-veteran-marriage-end-info-title'],
          depends: formData =>
            formData.claimantRelationship === 'SURVIVING_SPOUSE' &&
            !formData.marriedToVeteranAtTimeOfDeath,
          uiSchema: householdInformation.marriageToVeteranEndInfo.uiSchema,
          schema: householdInformation.marriageToVeteranEndInfo.schema,
        },
        marriageToVeteranEndDeathInfo: {
          path: 'marriage-to-veteran/end-death-location',
          title: 'Where did the Veteran’s death occur?',
          depends: formData =>
            formData.claimantRelationship === 'SURVIVING_SPOUSE' &&
            formData.marriedToVeteranAtTimeOfDeath,
          uiSchema: householdInformation.marriageToVeteranEndDeathInfo.uiSchema,
          schema: householdInformation.marriageToVeteranEndDeathInfo.schema,
        },
        marriageStatus: {
          path: 'marriage-status',
          title: 'Living together',
          depends: formData =>
            formData.claimantRelationship === 'SURVIVING_SPOUSE',
          uiSchema: householdInformation.marriageStatus.uiSchema,
          schema: householdInformation.marriageStatus.schema,
        },
        reasonForSeparation: {
          path: 'reason-for-separation',
          title: content['household-separation-reason-title'],
          depends: formData =>
            formData.claimantRelationship === 'SURVIVING_SPOUSE' &&
            formData.livedContinuouslyWithVeteran === false,
          uiSchema: householdInformation.reasonForSeparation.uiSchema,
          schema: householdInformation.reasonForSeparation.schema,
        },
        separationDetails: {
          path: 'reason-for-separation/details',
          title: content['household-separation-details-title'],
          depends: formData => {
            return (
              formData.claimantRelationship === 'SURVIVING_SPOUSE' &&
              formData.livedContinuouslyWithVeteran === false &&
              formData?.separationDueToAssignedReasons &&
              formData?.separationDueToAssignedReasons !== 'MEDICAL_FINANCIAL'
            );
          },
          uiSchema: householdInformation.separationDetails.uiSchema,
          schema: householdInformation.separationDetails.schema,
        },
        remarriage: {
          path: 'remarriage',
          title: content['household-remarriage-title'],
          depends: formData =>
            formData.claimantRelationship === 'SURVIVING_SPOUSE',
          uiSchema: householdInformation.remarriage.uiSchema,
          schema: householdInformation.remarriage.schema,
        },
        veteranChildren: {
          path: 'children-of-veteran',
          title: content['household-veteran-children-title'],
          depends: formData =>
            formData.claimantRelationship === 'SURVIVING_SPOUSE',
          uiSchema: householdInformation.veteranChildren.uiSchema,
          schema: householdInformation.veteranChildren.schema,
        },
        spouseMarriages: {
          path: 'previous-marriages',
          title: content['household-spouse-marriages-title'],
          depends: formData =>
            formData.claimantRelationship === 'SURVIVING_SPOUSE',
          uiSchema: householdInformation.spouseMarriages.uiSchema,
          schema: householdInformation.spouseMarriages.schema,
        },
        ...householdInformation.veteranMarriagesPages,
        ...householdInformation.previousMarriagesPages,
        dependentsCount: {
          path: 'household/dependents-count',
          title: content['household-dependents-count-title'],
          uiSchema: householdInformation.dependentsCount.uiSchema,
          schema: householdInformation.dependentsCount.schema,
        },
        additionalDependents: {
          path: 'household/additional-dependents',
          title: 'Additional form required',
          depends: householdInformation.needsAdditionalDependentsForm,
          uiSchema: householdInformation.additionalDependents.uiSchema,
          schema: householdInformation.additionalDependents.schema,
        },
        dependentChildName: {
          path: 'household/dependent/name-and-information',
          title: content['household-dependents-array-child-name-title'],
          depends: householdInformation.hasDependentChildToAdd,
          uiSchema: householdInformation.dependentName.uiSchema,
          schema: householdInformation.dependentName.schema,
        },
        dependentBirthInformation: {
          path: 'household/dependent/birth-information',
          title: 'Child’s birth information',
          depends: householdInformation.hasDependentChildToAdd,
          uiSchema: householdInformation.dependentBirthInformation.uiSchema,
          schema: householdInformation.dependentBirthInformation.schema,
        },
        dependentChildRelationship: {
          path: 'dependent-child/relationship',
          title: 'Child’s relationship to the Veteran',
          depends: householdInformation.hasDependentChildToAdd,
          uiSchema: householdInformation.dependentRelationship.uiSchema,
          schema: householdInformation.dependentRelationship.schema,
        },
        dependentChildInformation: {
          path: 'dependent-child/information',
          title: 'Additional information about the child',
          depends: householdInformation.hasDependentChildToAdd,
          uiSchema: householdInformation.dependentInformation.uiSchema,
          schema: householdInformation.dependentInformation.schema,
        },
      },
    },

    // Financial Information
    financialInformation: {
      title: content['financial-info-chapter-title'],
      pages: {
        totalAssets: {
          title: content['financial-info-total-assets-title'],
          path: 'financial-information/total-assets',
          depends: formData => formData?.claims?.survivorsPension === true,
          uiSchema: financialInformationUpdate.totalAssets.uiSchema,
          schema: financialInformationUpdate.totalAssets.schema,
        },
        transferredAssets: {
          title: content['financial-info-transferred-assets-title'],
          path: 'financial-information/transferred-assets',
          depends: formData => formData?.claims?.survivorsPension === true,
          uiSchema: financialInformationUpdate.transferredAssets.uiSchema,
          schema: financialInformationUpdate.transferredAssets.schema,
        },
        homeOwnership: {
          title: content['financial-info-home-ownership-title'],
          path: 'financial-information/homeownership',
          depends: formData => formData?.claims?.survivorsPension === true,
          uiSchema: financialInformationUpdate.homeOwnership.uiSchema,
          schema: financialInformationUpdate.homeOwnership.schema,
        },
        landLotSize: {
          title: content['financial-info-land-lot-title'],
          path: 'financial-information/land-lot-size',
          depends: formData =>
            formData?.homeOwnership === true &&
            formData?.claims?.survivorsPension === true,
          uiSchema: financialInformationUpdate.landLotSize.uiSchema,
          schema: financialInformationUpdate.landLotSize.schema,
        },
        marketableLand: {
          title: content['financial-info-marketable-land-title'],
          path: 'financial-information/marketable-land',
          depends: formData =>
            formData?.homeOwnership === true &&
            formData?.homeAcreageMoreThanTwo === true &&
            formData?.claims?.survivorsPension === true,
          uiSchema: financialInformationUpdate.marketableLand.uiSchema,
          schema: financialInformationUpdate.marketableLand.schema,
        },
        incomeSources: {
          title: content['financial-info-income-sources-title'],
          path: 'financial-information/income-sources',
          depends: formData => formData?.claims?.survivorsPension === true,
          uiSchema: financialInformationUpdate.incomeSources.uiSchema,
          schema: financialInformationUpdate.incomeSources.schema,
        },
        ...financialInformation.grossMonthlyIncomePages,
        ...financialInformation.careExpensesPages,
        ...financialInformation.medicalExpensesPages,
      },
    },
    // financialInformation: {
    //   title: content['financial-info-chapter-title'],
    //   pages: {
    //     incomeAndAssets: {
    //       title: content['financial-info-income-assets-title'],
    //       path: 'financial-information/income-and-assets',
    //       depends: formData => formData?.claims?.survivorsPension === true,
    //       uiSchema: financialInformation.incomeAndAssets.uiSchema,
    //       schema: financialInformation.incomeAndAssets.schema,
    //     },
    //     totalAssets: {
    //       title: content['financial-info-total-assets-title'],
    //       path: 'financial-information/total-assets',
    //       depends: formData =>
    //         formData?.totalNetWorth === false &&
    //         formData?.claims?.survivorsPension === true,
    //       uiSchema: financialInformation.totalAssets.uiSchema,
    //       schema: financialInformation.totalAssets.schema,
    //     },
    //     transferredAssets: {
    //       title: content['financial-info-transferred-title'],
    //       path: 'financial-information/transferred-assets',
    //       depends: formData => formData?.claims?.survivorsPension === true,
    //       uiSchema: financialInformation.transferredAssets.uiSchema,
    //       schema: financialInformation.transferredAssets.schema,
    //     },
    //     homeOwnership: {
    //       title: content['financial-info-home-ownership-title'],
    //       path: 'financial-information/homeownership',
    //       depends: formData => formData?.claims?.survivorsPension === true,
    //       uiSchema: financialInformation.homeOwnership.uiSchema,
    //       schema: financialInformation.homeOwnership.schema,
    //     },
    //     landLotSize: {
    //       title: content['financial-info-land-lot-title'],
    //       path: 'financial-information/land-lot-size',
    //       depends: formData =>
    //         formData?.homeOwnership === true &&
    //         formData?.claims?.survivorsPension === true,
    //       uiSchema: financialInformation.landLotSize.uiSchema,
    //       schema: financialInformation.landLotSize.schema,
    //     },
    //     additionalLandValue: {
    //       title: content['financial-info-additional-land-title'],
    //       path: 'financial-information/additional-land-value',
    //       depends: formData =>
    //         formData?.homeAcreageMoreThanTwo === true &&
    //         formData?.claims?.survivorsPension === true,
    //       uiSchema: financialInformation.additionalLandValue.uiSchema,
    //       schema: financialInformation.additionalLandValue.schema,
    //     },
    //     marketableLand: {
    //       title: content['financial-info-marketable-land-title'],
    //       path: 'financial-information/marketable-land',
    //       depends: formData =>
    //         formData?.homeAcreageMoreThanTwo === true &&
    //         formData?.claims?.survivorsPension === true,
    //       uiSchema: financialInformation.marketableLand.uiSchema,
    //       schema: financialInformation.marketableLand.schema,
    //     },
    //     incomeSources: {
    //       title: content['financial-info-income-sources-title'],
    //       path: 'financial-information/income-sources',
    //       depends: formData => formData?.claims?.survivorsPension === true,
    //       uiSchema: financialInformation.incomeSources.uiSchema,
    //       schema: financialInformation.incomeSources.schema,
    //     },
    //     ...financialInformation.grossMonthlyIncomePages,
    //     ...financialInformation.careExpensesPages,
    //     ...financialInformation.medicalExpensesPages,
    //   },
    // },

    // Additional Information
    additionalInformation: {
      title: content['additional-info-chapter-title'],
      pages: {
        directDeposit: {
          title: content['additional-info-deposit-title'],
          path: 'additional-information/direct-deposit',
          uiSchema: additionalInformation.directDeposit.uiSchema,
          schema: additionalInformation.directDeposit.schema,
        },
        directDepositAccount: {
          title: content['additional-info-account-title'],
          path: 'additional-information/direct-deposit/account',
          depends: additionalInformation.directDepositAccount.depends,
          uiSchema: additionalInformation.directDepositAccount.uiSchema,
          schema: additionalInformation.directDepositAccount.schema,
        },
        otherPaymentOptions: {
          title: content['additional-info-other-payment-title'],
          path: 'additional-information/other-payment-options',
          depends: additionalInformation.otherPaymentOptions.depends,
          uiSchema: additionalInformation.otherPaymentOptions.uiSchema,
          schema: additionalInformation.otherPaymentOptions.schema,
        },
        supportingDocuments: {
          title: content['additional-info-supporting-documents-title'],
          path: 'additional-information/supporting-documents',
          uiSchema: additionalInformation.supportingDocuments.uiSchema,
          schema: additionalInformation.supportingDocuments.schema,
        },
        uploadDocuments: {
          title: content['additional-info-upload-documents-title'],
          path: 'additional-information/upload-documents',
          depends: formData => formData?.['view:idpEnabled'] !== true,
          uiSchema: additionalInformation.uploadDocuments.uiSchema,
          schema: additionalInformation.uploadDocuments.schema,
          scrollAndFocusTarget,
        },
        uploadDocumentsWithCave: {
          title: 'Upload documents',
          path: 'additional-information/upload-documents-cave',
          depends: formData =>
            formData?.['view:idpEnabled'] === true &&
            !formData?.survivorsBenefitsForm2026VersionEnabled,
          uiSchema: caveUploadDocumentsPage.uiSchema,
          schema: caveUploadDocumentsPage.schema,
          CustomPageReview: caveUploadDocumentsPage.CustomPageReview,
        },
        confirmVeteranInfo: {
          title: 'Confirm Veteran information',
          path: 'additional-information/confirm-veteran-info',
          depends: formData =>
            formData?.['view:idpEnabled'] === true &&
            !formData?.survivorsBenefitsForm2026VersionEnabled &&
            formData?.files?.some(f => f?.idpArtifacts) &&
            hasVeteranInfoConflicts(formData),
          uiSchema: caveConfirmVeteranInfoPage.uiSchema,
          schema: caveConfirmVeteranInfoPage.schema,
        },
        confirmMilitaryHistory: {
          title: 'Confirm military history',
          path: 'additional-information/confirm-military-history',
          depends: formData =>
            formData?.['view:idpEnabled'] === true &&
            !formData?.survivorsBenefitsForm2026VersionEnabled &&
            formData?.files?.some(f => f?.idpArtifacts) &&
            hasMilitaryHistoryConflicts(formData),
          uiSchema: caveConfirmMilitaryHistoryPage.uiSchema,
          schema: caveConfirmMilitaryHistoryPage.schema,
        },
        artifactReview: {
          title: 'Review uploaded documents',
          path: 'additional-information/document-review',
          depends: formData =>
            formData?.['view:idpEnabled'] === true &&
            !formData?.survivorsBenefitsForm2026VersionEnabled &&
            formData?.files?.some(f => f?.idpArtifacts),
          CustomPage: ArtifactReviewPage,
          CustomPageReview: null,
          uiSchema: {},
          schema: blankSchema,
        },
      },
    },
  },
};

export default applyForceDivWrapperToFormConfig(formConfig);
